import React, { useCallback, useEffect, useMemo, useState } from "react";
import useFormulaEditor from "../../../hooks/useFormulaEditor";
import useFormulaValidation from "../../../hooks/useFormulaValidation";
import useFormulaTemplates, {
  type FormulaTemplateOption,
} from "../../../hooks/useFormulaTemplates";
import useUpdateFormula from "../../../hooks/useUpdateFormula";
import useVariableDefinitions from "../../../hooks/useVariableDefinitions";
import useOpDataCatalog from "../../../hooks/useOpDataCatalog";
import useTemplateSelection from "../../../hooks/useTemplateSelection";
import type { FormulaValidationResult } from "../../../formula/validateFormula";
import type { TokenKind } from "../../../formula/formulaToken";
import { useStore } from "@/app/store";
import { useAppToast } from "@/components/AppToast";
import { saveFailedMessage } from "../CostPanel.constants";
import FormulaEditor from "./FormulaEditor.component";
import RemoveVariablesConfirmationPopup from "./RemoveVariablesConfirmationPopup/RemoveVariablesConfirmationPopup.component";
import { computeRemovedCodes } from "./computeRemovedCodes";
import type { PaletteVariable } from "./FormulaEditor.types";

export interface FormulaEditorContainerProps {
  active: boolean;
  panelTitle: string;
  subCategoryId: string;
  dimensionValue: string | null;
  savedExpression: string;
  sourceTemplateId: string | null;
  referencedVariableNames: Record<string, string>;
  onClose: () => void;
  onToggleFormulaMode: () => void;
  onConflict: () => void;
}

const FormulaEditorContainer: React.FC<FormulaEditorContainerProps> = ({
  active,
  panelTitle,
  subCategoryId,
  dimensionValue,
  savedExpression,
  sourceTemplateId,
  referencedVariableNames,
  onClose,
  onToggleFormulaMode,
  onConflict,
}) => {
  const editor = useFormulaEditor(savedExpression);
  const {
    templates,
    loading: isLoadingTemplates,
    error: templatesError,
  } = useFormulaTemplates(subCategoryId);
  const {
    variables: pool,
    loading: isLoadingKamVariables,
    error: poolError,
  } = useVariableDefinitions();
  const {
    opDataVariables: catalog,
    loading: isLoadingOpData,
    error: opDataError,
  } = useOpDataCatalog();
  const { updateFormula, isSaving } = useUpdateFormula(onConflict);
  const { addToast } = useAppToast();

  useEffect(() => {
    if (!poolError) return;
    addToast({
      appearance: "danger",
      message: "Could not load variables. Please try again.",
    });
  }, [poolError, addToast]);

  useEffect(() => {
    if (!opDataError) return;
    addToast({
      appearance: "warning",
      message:
        "Could not load Operational Data. You can still use this bucket's variables.",
    });
  }, [opDataError, addToast]);

  useEffect(() => {
    if (!templatesError) return;
    addToast({
      appearance: "warning",
      message:
        "Could not load formula templates. You can still enter a custom formula.",
    });
  }, [templatesError, addToast]);

  const allowedVariables = useMemo<PaletteVariable[]>(
    () =>
      pool.map((variable) => ({
        code: variable.code,
        name: variable.displayName,
      })),
    [pool],
  );

  const opDataVariables = useMemo<PaletteVariable[]>(
    () =>
      catalog.map((variable) => ({
        code: variable.code,
        name: variable.displayName,
      })),
    [catalog],
  );

  const chipNameByCode = useMemo<Record<string, string>>(() => {
    const kamNames: Record<string, string> = {};
    for (const variable of pool) kamNames[variable.code] = variable.displayName;

    const opDataNames: Record<string, string> = {};
    for (const variable of catalog)
      opDataNames[variable.code] = variable.displayName;
    return { ...kamNames, ...referencedVariableNames, ...opDataNames };
  }, [referencedVariableNames, pool, catalog]);

  const allowedCodes = useMemo(
    () => allowedVariables.map((v) => v.code),
    [allowedVariables],
  );
  const opDataCodes = useMemo(
    () => opDataVariables.map((v) => v.code),
    [opDataVariables],
  );
  const { result } = useFormulaValidation(
    editor.expression,
    allowedCodes,
    opDataCodes,
  );

  const loadDefaultExpression = useCallback(
    (expression: string) => editor.selectTemplate(expression),
    [editor],
  );
  const {
    selection,
    isSelectionPending,
    selectTemplate,
    selectCustom,
    markCustomized,
  } = useTemplateSelection({
    sourceTemplateId,
    templates,
    isLoadingTemplates,
    savedExpression,
    onLoadDefaultExpression: loadDefaultExpression,
  });

  // Backend rejection, rendered through the same error path as the inline validator. Cleared on any edit.
  const [saveError, setSaveError] = useState<FormulaValidationResult | null>(
    null,
  );
  const [showValidation, setShowValidation] = useState(false);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  const clearErrors = useCallback(() => {
    setSaveError(null);
    setShowValidation(false);
  }, []);

  const markEdited = useCallback(() => {
    clearErrors();
    markCustomized();
  }, [clearErrors, markCustomized]);

  const addToken = useCallback(
    (kind: TokenKind, value: string) => {
      markEdited();
      editor.addToken(kind, value);
    },
    [editor, markEdited],
  );
  const removeToken = useCallback(
    (uid: string) => {
      markEdited();
      editor.removeToken(uid);
    },
    [editor, markEdited],
  );
  const clearAll = useCallback(() => {
    markEdited();
    editor.clearAll();
  }, [editor, markEdited]);
  const reorder = useCallback(
    (activeUid: string, beforeUid: string | null) => {
      markEdited();
      editor.reorder(activeUid, beforeUid);
    },
    [editor, markEdited],
  );
  const insertToken = useCallback(
    (kind: TokenKind, value: string, beforeUid: string | null) => {
      markEdited();
      editor.insertToken(kind, value, beforeUid);
    },
    [editor, markEdited],
  );

  const handleSelectTemplate = (template: FormulaTemplateOption) => {
    clearErrors();
    editor.selectTemplate(template.expression);
    selectTemplate(template.id);
  };

  const handleSelectCustom = () => {
    clearErrors();
    editor.selectCustom();
    selectCustom();
  };

  const runSave = useCallback(async () => {
    const calculationId = useStore.getState().calculation?.id;
    if (!calculationId) return;
    const outcome = await updateFormula({
      calculationId,
      subCategoryId,
      expression: editor.expression,
      dimensionValue,
    });
    switch (outcome.kind) {
      case "saved":
        onToggleFormulaMode();
        addToast({
          appearance: "success",
          message: "Formula has been updated.",
        });
        break;
      case "rejected":
        // Render the backend rejection through the inline validator's error path.
        setSaveError({ ok: false, ...outcome.error });
        break;
      case "failed":
        addToast({ appearance: "danger", message: saveFailedMessage });
        break;
      case "conflict":
      case "opDataNotPopulated":
        break;
    }
  }, [
    updateFormula,
    subCategoryId,
    editor.expression,
    dimensionValue,
    onToggleFormulaMode,
    addToast,
  ]);

  const handleSave = useCallback(async () => {
    if (isLoadingKamVariables || isLoadingOpData) return;
    setShowValidation(true);
    if (!result.ok) return;

    const removed = computeRemovedCodes(savedExpression, editor.tokens);
    if (removed.size > 0) {
      setShowRemoveConfirm(true);
      return;
    }

    await runSave();
  }, [
    isLoadingKamVariables,
    isLoadingOpData,
    result,
    savedExpression,
    editor.tokens,
    runSave,
  ]);

  const handleConfirmRemoval = useCallback(async () => {
    await runSave();
    setShowRemoveConfirm(false);
  }, [runSave]);

  const handleCancelRemoval = useCallback(
    () => setShowRemoveConfirm(false),
    [],
  );

  // Only show inline validation error after a save attempt; backend rejection always shows.
  const effectiveResult =
    saveError ?? (showValidation ? result : { ok: true as const });

  const effectiveSaveDisabled =
    saveError !== null || isLoadingKamVariables || isLoadingOpData;

  return (
    <>
      <FormulaEditor
        active={active}
        panelTitle={panelTitle}
        templates={templates}
        paletteVariables={allowedVariables}
        opDataVariables={opDataVariables}
        chipNameByCode={chipNameByCode}
        isLoadingKamVariables={isLoadingKamVariables}
        isLoadingOpData={isLoadingOpData}
        allowedCodes={allowedCodes}
        opDataCodes={opDataCodes}
        tokens={editor.tokens}
        isCustom={selection.kind === "custom"}
        isSelectionPending={isSelectionPending}
        isDirty={editor.isDirty}
        selectedTemplateId={selection.kind === "template" ? selection.id : null}
        validation={effectiveResult}
        isSaveDisabled={effectiveSaveDisabled}
        isSaving={isSaving}
        onClose={onClose}
        onToggleFormulaMode={onToggleFormulaMode}
        onAddToken={addToken}
        onInsertToken={insertToken}
        onRemoveToken={removeToken}
        onReorder={reorder}
        onClearAll={clearAll}
        onSelectTemplate={handleSelectTemplate}
        onSelectCustom={handleSelectCustom}
        onSave={handleSave}
      />
      <RemoveVariablesConfirmationPopup
        active={showRemoveConfirm}
        isSaving={isSaving}
        onConfirm={handleConfirmRemoval}
        onCancel={handleCancelRemoval}
      />
    </>
  );
};

export default FormulaEditorContainer;
